import { QueryClientProvider } from "@tanstack/react-query";
import { Route, Routes } from "react-router-dom";
import { queryClient } from "@/api";
import { MaintenanceBanner, ToastProvider } from "@/ui";
import {
    CompanyProfilePage,
    LoginErrorPage,
    LoginPage,
    LogoutPage,
    PostSignupPage,
    ResetPasswordPage,
    SignupCompletePage,
    SignupPage,
} from "./pages";
import { SessionController } from "./SessionController";
import { datadogRum } from "@datadog/browser-rum";
import flagsmith from "flagsmith";
import { FlagsmithProvider } from "flagsmith/react";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Provider } from "jotai";
import { Auth0Provider } from "@auth0/auth0-react";
import { useRouter, V1RedirectWithPath, useFlags } from "@/utils";
import SignupRedirect from "./pages/SignupRedirect";
import CompleteInvitationPage from "./pages/CompleteInvitationPage";
import { LicenseManager } from "ag-charts-enterprise";

LicenseManager.setLicenseKey(process.env.AG_CHARTS_LICENSE_KEY || "");

const flagsmithOptions = {
    environmentID: process.env.FLAGSMITH_CLIENT_SIDE_ENVIRONMENT_KEY || "",
    api: process.env.FLAGSMITH_API_URL || "",
    datadogRum: {
        client: datadogRum,
        trackTraits: true,
    },
    headers: {
        "x-environment-key":
            process.env.FLAGSMITH_CLIENT_SIDE_ENVIRONMENT_KEY || "",
    },
};
let cookieDomain = ".localhost";
if (process.env.NODE_ENV === "staging") {
    cookieDomain = ".humcapital.info";
} else if (process.env.NODE_ENV === "production") {
    cookieDomain = ".humcapital.com";
}

function MainApp() {
    const router = useRouter();
    const flags = useFlags();
    const banner = flags.enabled("maintenance-banner")
        ? (flags.value("maintenance-banner") as Record<string, any>)
        : null;

    return (
        <>
            <Auth0Provider
                domain={process.env.AUTH0_DOMAIN || ""}
                clientId={process.env.AUTH0_CLIENT_ID || ""}
                authorizationParams={{
                    audience: process.env.AUTH0_AUDIENCE || "",
                }}
                useRefreshTokensFallback
                useRefreshTokens
                cookieDomain={cookieDomain}
                onRedirectCallback={() => {
                    router.navigate(router.asPath);
                }}
            >
                {flags.enabled("maintenance-banner") && (
                    <MaintenanceBanner
                        title={banner?.title}
                        message={banner?.message}
                    />
                )}
                <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/raise-or-invest" element={<SignupPage />} />
                    <Route path="/signup/:role" element={<SignupRedirect />} />
                    <Route
                        path="/post-signup/:role"
                        element={<PostSignupPage />}
                    />
                    <Route
                        path="/signup-complete"
                        element={<SignupCompletePage />}
                    />
                    <Route
                        path="/complete-invitation"
                        element={<CompleteInvitationPage />}
                    />
                    <Route path="/error" element={<LoginErrorPage />} />
                    <Route path="/logout" element={<LogoutPage />} />
                    <Route
                        path="/public/:token"
                        element={<CompanyProfilePage />}
                    />
                    <Route
                        path="/reset-password"
                        element={<ResetPasswordPage />}
                    />
                    <Route path="/legacy" element={<V1RedirectWithPath />} />
                    <Route path="*" element={<SessionController />} />
                </Routes>
            </Auth0Provider>
            <ToastProvider />
        </>
    );
}

export function RootContainer() {
    return (
        <QueryClientProvider client={queryClient}>
            <Provider>
                <FlagsmithProvider
                    options={flagsmithOptions}
                    flagsmith={flagsmith}
                >
                    <MainApp />
                </FlagsmithProvider>
                <ReactQueryDevtools initialIsOpen={false} />
            </Provider>
        </QueryClientProvider>
    );
}
